import {
  BankAccountFragmentFragment,
  useGetBankAccountQuery,
  useSearchBankAccountsQuery,
  ZyusetuFieldKeyFragmentFragment, ZyusetuFieldKeyType,
} from "@/graphql/graphql";
import {FormSchema} from "@/components/pages/Zyusetu/ZyusetuEditorSection.tsx";
import {AutocompleteInput} from "@/components/input/Autocomplete.tsx";
import {useEffect, useState} from "react";
import {zyusetuValueIntoZodValue} from "@/hooks/useGetFirstContent";
import {useFormContext, useWatch} from "react-hook-form";
import {createFilterOptions, useTheme} from "@mui/material";
import {Plus} from "lucide-react";
import {useQueryClient} from "@tanstack/react-query";
import {CreateNewBankAccountDialog} from "@/components/dialog/CreateNewBankAccountDialog.tsx";

const filter = createFilterOptions<Partial<BankAccountFragmentFragment>>();

interface ZyusetuBankAccountFieldProps {
  fieldKey: ZyusetuFieldKeyFragmentFragment;
  onFocus: () => void;
  updateField: (
    keyId: string,
    keyName: string,
    value: FormSchema[string],
  ) => Promise<void>;
  isAvailable: boolean;
}

export function ZyusetuBankAccountField({
                                          fieldKey,
                                          updateField,
                                          isAvailable,
                                        }: ZyusetuBankAccountFieldProps) {
  const [bankAccountString, setBankAccountString] = useState("");
  const [bankAccount, setBankAccount] =
    useState<Partial<BankAccountFragmentFragment> | null>();
  const search = useSearchBankAccountsQuery({
    searchString: bankAccountString,
  })
  
  const queryKey = useSearchBankAccountsQuery.getKey({
    searchString: bankAccountString,
  })
  const queryClient = useQueryClient()
  
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  
  const {control} = useFormContext<FormSchema<ZyusetuFieldKeyType.AutoCompleteBankAccount>>();
  
  const watch = useWatch({
    name: fieldKey.keyName,
    control,
  });
  
  const {data} = useGetBankAccountQuery(
    {
      id: watch.value!,
    },
    {
      enabled: !!watch.value,
    }
  );
  
  useEffect(() => {
    if (data?.getBankAccount) {
      setBankAccount(data?.getBankAccount);
    }
  }, [data?.getBankAccount]);
  
  const theme = useTheme()
  
  return (
    <>
      {openCreateDialog &&
        <CreateNewBankAccountDialog
          open={openCreateDialog} onClose={() => setOpenCreateDialog(false)}
          onCreateSuccess={async (value) => {
            setBankAccount(value);
            await updateField(
              fieldKey.id,
              fieldKey.keyName,
              zyusetuValueIntoZodValue(fieldKey, value.id),
            );
            
            queryClient.setQueryData(queryKey, {
              searchManagementCompanies: [
                ...(search.data?.searchBankAccounts ?? []),
                value,
              ],
            });
          }}
        />}
      <AutocompleteInput
        onChange={async (v) => {
          if (!v) {
            setBankAccount(null);
            await updateField(
              fieldKey.id,
              fieldKey.keyName,
              zyusetuValueIntoZodValue(fieldKey, ""),
            );
            return;
          }
          if (v.id === "new") {
            setOpenCreateDialog(true);
            return
          }
          setBankAccount(v);
          await updateField(
            fieldKey.id,
            fieldKey.keyName,
            zyusetuValueIntoZodValue(fieldKey, (v.id) ?? ""),
          );
        }}
        filterOptions={(options = [], params) => {
          const filtered = filter(options, params);
          filtered.push({bankName: "\"新規追加\"", id: "new"});
          return filtered;
        }}
        renderOption={(props, option) => {
          if (option.id === "new") {
            return <li
              {...props}
              style={{
                color: theme.palette.primary.main,
              }}
            >
              <Plus/>
              新規追加
            </li>
          }
          
          return (
            <li {...props}>
              {option.bankName}({option.accountHolderName})#{option.accountNumber}
            </li>
          );
        }}
        disabled={!isAvailable}
        loading={search.isLoading}
        isOptionEqualToValue={(option, value) => option.id === value?.id}
        value={bankAccount as Partial<BankAccountFragmentFragment> ?? null}
        multiple={false}
        getOptionLabel={(option) => `${option.bankName}(${option.accountHolderName})#${option.accountNumber}`}
        fetchOptions={async (words: string) => {
          setBankAccountString(words);
        }}
        options={search.data?.searchBankAccounts || []}
      /></>
  );
}
